"use client";

import clsx from "clsx";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screens 5.1 & 5.2 — T3 origination via Pampagro's sourcing agent.
 *
 *   5.1 PampagroRfq          — Pampagro's sourcing-agent UI posts an RFQ
 *                              through Orbitra (specs, qty, target market,
 *                              Incoterms FOB Abu Dhabi Port)
 *   5.2 GulfPhosphateReply   — Gulf Phosphate's inventory agent receives +
 *                              counter-offers (8,500 t available, USD 425/t)
 *
 * Both are chat-style panels framed inside an Orbitra surface — the same
 * pattern as T1's WhatsApp mock but inside the platform, not on a
 * consumer messaging app.
 */

export function PampagroRfq({ className }: { className?: string }) {
  const t = useTranslations("t3Scenario");
  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between border-b border-state-verified/15 pb-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("rfqSourcingAgentLabel")}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("rfqHeadingPost")}</h3>
          </div>
          <span className="rounded-full border border-state-verified/40 bg-state-verified/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-verified">
            {t("rfqBroadcastTag")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("fieldCommodityLabel")}       value={t("fieldCommodityValue")} />
          <Field label={t("fieldQuantityLabel")}        value={t("fieldQuantityValue")} />
          <Field label={t("fieldTargetMarketLabel")}    value={t("fieldTargetMarketValue")} />
          <Field label={t("fieldIncotermsLabel")}       value={t("fieldIncotermsValue")} mono />
          <Field label={t("fieldTargetPriceLabel")}     value={t("fieldTargetPriceValue")} />
          <Field label={t("fieldLeadTimeLabel")}        value={t("fieldLeadTimeValue")} />
          <Field label={t("fieldCounterpartyReqLabel")} value={t("fieldCounterpartyReqValue")} />
          <Field label={t("fieldPostedViaLabel")}       value={t("fieldPostedViaValue")} mono />
        </div>

        <div className="mt-5 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-verified">{t("rfqSourcingAgentLabel")}</p>
          <p className="mt-1 text-orbitra-paper italic">"{t("agentQuoteRfq")}"</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("sidebarRoutingTitle")}</p>
          <p className="mt-2 text-orbitra-paper">{t("sidebarRoutingBody")}</p>
          <p className="mt-2 text-[10px] text-orbitra-muted">{t("sidebarRoutingFooter")}</p>
        </div>
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("sidebarOrbidTitle")}</p>
          <p className="mt-2 font-mono text-orbitra-paper" dir="ltr">corbid:5493-pampagro-br</p>
          <p className="mt-1 text-[10px] text-orbitra-muted">{t("sidebarOrbidStats")}</p>
        </div>
      </div>
    </div>
  );
}

export function GulfPhosphateReply({ className }: { className?: string }) {
  const t = useTranslations("t3Scenario");
  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      <div className="glass col-span-2 flex flex-col p-5">
        <div className="flex items-center justify-between border-b border-state-traced/15 pb-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("replyInventoryAgentLabel")}</p>
            <h3 className="mt-1 font-display text-lg font-semibold text-orbitra-paper">{t("replyOfferHeading")}</h3>
          </div>
          <span className="rounded-full border border-state-traced/40 bg-state-traced/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-traced">
            {t("replyOfferTag")}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 text-[12px]">
          <Field label={t("replyFieldAvailableStock")}   value={t("replyFieldAvailableStockValue")} />
          <Field label={t("replyFieldOfferPrice")}       value={t("replyFieldOfferPriceValue")} tone="ok" />
          <Field label={t("replyFieldQuality")}          value={t("replyFieldQualityValue")} />
          <Field label={t("replyFieldLoadingWindow")}    value={t("replyFieldLoadingWindowValue")} />
          <Field label={t("replyFieldCarrier")}          value={t("replyFieldCarrierValue")} />
          <Field label={t("replyFieldPayment")}          value={t("replyFieldPaymentValue")} />
          <Field label={t("replyFieldCerts")}            value={t("replyFieldCertsValue")} />
          <Field label={t("replyFieldTotal")}            value={t("replyFieldTotalValue")} tone="ok" />
        </div>

        <div className="mt-5 rounded-md border border-state-traced/30 bg-state-traced/8 p-3 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-traced">{t("replyInventoryAgentLabel")}</p>
          <p className="mt-1 text-orbitra-paper italic">"{t("replyAgentQuote")}"</p>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("replySidebarMercatorTitle")}</p>
          <p className="mt-2 text-orbitra-paper">"{t("replySidebarMercatorBody")}"</p>
        </div>
        <div className="glass p-4 text-[11px]">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("replySidebarCounterOfferTitle")}</p>
          <p className="mt-2 text-orbitra-paper">{t("replySidebarCounterOfferBody")}</p>
          <p className="mt-2 text-[10px] text-orbitra-muted">{t("replySidebarCounterOfferFooter")}</p>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  tone,
  mono = false,
}: {
  label: string;
  value: string;
  tone?: "ok";
  mono?: boolean;
}) {
  return (
    <div
      className={clsx(
        "rounded-md border bg-orbitra-ink/40 px-3 py-2",
        tone === "ok" ? "border-policy-pass/40" : "border-state-verified/20",
      )}
    >
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <p className={clsx("mt-0.5 text-orbitra-paper", mono && "font-mono")}>{value}</p>
    </div>
  );
}
